import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { usePlans } from '@/hooks/usePlans';
import { usePlanBonuses } from '@/hooks/usePlanBonuses';
import { Badge } from '@/components/ui/badge';
import { Gift, TrendingUp, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';

interface TenantBonusDialogProps {
  tenant: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TenantBonusDialog({ tenant, open, onOpenChange }: TenantBonusDialogProps) {
  const { plans } = usePlans();
  const { activeBonus, applyBonus, revertBonus } = usePlanBonuses(tenant?.id);
  
  const [bonusPlanId, setBonusPlanId] = useState('');
  const [returnPlanId, setReturnPlanId] = useState('');
  const [bonusPeriod, setBonusPeriod] = useState<'monthly' | 'annual' | 'lifetime'>('monthly');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');

  const handleApply = async () => {
    if (!bonusPlanId || !returnPlanId || !endDate) {
      return;
    }

    await applyBonus.mutateAsync({
      tenantId: tenant.id,
      bonusPlanId,
      returnPlanId,
      bonusPeriod,
      endDate,
      notes,
    });

    onOpenChange(false);
  };

  const handleRevert = async () => {
    await revertBonus.mutateAsync(tenant.id);
    onOpenChange(false);
  };

  if (!tenant) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Bonificação de Plano - {tenant.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Plano Atual */}
          <div className="p-3 bg-muted rounded-lg">
            <Label className="text-xs text-muted-foreground">Plano Atual</Label>
            <p className="font-medium">{tenant.subscription_plans?.name || 'Sem plano'}</p>
            {activeBonus && (
              <Badge variant="default" className="mt-2">
                <Gift className="h-3 w-3 mr-1" />
                Bonificado até {format(new Date(activeBonus.end_date), 'dd/MM/yyyy')}
              </Badge>
            )}
          </div>

          {activeBonus ? (
            /* Bonificação Ativa - Opção de Reverter */
            <div className="space-y-3">
              <div className="p-3 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-start justify-between">
                  <div>
                    <Label className="text-green-700 dark:text-green-300 font-medium">
                      Bonificação Ativa
                    </Label>
                    <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                      Plano: {activeBonus.bonus_plan?.name}
                    </p>
                    <p className="text-sm text-green-600 dark:text-green-400">
                      Retorna para: {activeBonus.return_plan?.name}
                    </p>
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                      Válido até: {format(new Date(activeBonus.end_date), "dd/MM/yyyy 'às' HH:mm")}
                    </p>
                  </div>
                </div>
              </div>

              <Button
                variant="destructive"
                className="w-full"
                onClick={handleRevert}
                disabled={revertBonus.isPending}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                {revertBonus.isPending ? 'Revertendo...' : 'Reverter Bonificação Agora'}
              </Button>
            </div>
          ) : (
            /* Formulário de Nova Bonificação */
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bonus_plan">Plano Bonificado (Upgrade)</Label>
                <Select value={bonusPlanId} onValueChange={setBonusPlanId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o plano bonificado" />
                  </SelectTrigger>
                  <SelectContent>
                    {plans?.filter(p => p.is_active).map(plan => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name} - R$ {plan.price_monthly}/mês
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="return_plan">Plano de Retorno</Label>
                <Select value={returnPlanId} onValueChange={setReturnPlanId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Plano após expiração" />
                  </SelectTrigger>
                  <SelectContent>
                    {plans?.filter(p => p.is_active).map(plan => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="period">Período da Bonificação</Label>
                  <Select value={bonusPeriod} onValueChange={(v: any) => setBonusPeriod(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Mensal</SelectItem>
                      <SelectItem value="annual">Anual</SelectItem>
                      <SelectItem value="lifetime">Vitalício</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="end_date">Data Limite</Label>
                  <Input
                    id="end_date"
                    type="datetime-local"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Observações (Opcional)</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ex: Bonificação por parceria estratégica..."
                  rows={3}
                />
              </div>

              <Button
                className="w-full"
                onClick={handleApply}
                disabled={!bonusPlanId || !returnPlanId || !endDate || applyBonus.isPending}
              >
                <TrendingUp className="h-4 w-4 mr-2" />
                {applyBonus.isPending ? 'Aplicando...' : 'Aplicar Bonificação'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
